import React, {useCallback, useContext, useMemo, useState} from 'react';
import PopUp from 'js/components/Common/PopUp/PopUp';
import {useCookies, withCookies} from 'react-cookie';
import useFetchData from 'js/hooks/useFetchData/useFetchData';
import DataValidator from 'js/components/Common/DataValidator/DataValidator';

import FetchData from './fetchData';
import FirstStage from './FirstStage/FirstStage';
import SecondStage, {CookieDescriptionsType} from './SecondStage/SecondStage';
import classes from './CookiePolicyPopup.module.pcss';
import {CommonContext} from 'js/context/CommonContext/CommonContext';
import Container from 'js/components/Common/Container/Container';
import MixPanelWrapper from 'js/servises/MixPanelWrapper';

const CookiePolicyPopup = () => {
    const [showPopup, setShowPopup] = useState<boolean>(false);
    const [, setCookie] = useCookies([]);
    const {isCustomerLogged} = useContext(CommonContext);
    const {REACT_APP_MIX_PANEL_INIT_COOKIE, REACT_APP_ACCEPTED_POLICY_COOKIE} = process.env || {};
    const [mainTitle, setMainTitle] = useState<string>('');
    const [secondaryTitle, setSecondaryTitle] = useState<string>('');
    const [mainInfo, setMainInfo] = useState<string>('');
    const [secondaryInfo, setSecondaryInfo] = useState<string>('');
    const [contentStage, setContentStage] = useState<1 | 2>(2);
    const [mixPanelCookies, setMixPanelCookies] = useState<boolean | undefined>(false);
    const [cookiesDescriptions, setCookiesDescriptions] = useState<CookieDescriptionsType>({
        analyticsCookiesInfo: '',
        analyticsCookiesTitle: '',
        essentialCookiesInfo: '',
        essentialCookiesTitle: ''
    });

    const contentConditionVar = useMemo(() => (
        mainTitle?.length && mainInfo?.length && secondaryTitle?.length && secondaryInfo?.length
    ), [mainInfo, mainTitle, secondaryTitle, secondaryInfo]);

    const cookieOption = useMemo(() => (
        {
            path: '/',
            domain: `.${location.hostname}`,
            maxAge: 315360000
        }
    ), []);

    const { isLoading, error } = useFetchData({
        query: FetchData,
        condition: true,
        updateData: (data) => {
            const result = data?.cookiePolicy?.CookiePolicyPopup;
            if (!result) {
                return;
            }
            setMainTitle(result?.titleCookiePolicy);
            setMainInfo(result?.mainInfoCookiePolicy);
            setSecondaryInfo(result?.secondaryInfoCookiePolicy);
            setSecondaryTitle(result?.secondaryTitleCookiePolicy);
            const {
                analyticsCookiesInfo,
                analyticsCookiesTitle,
                essentialCookiesInfo,
                essentialCookiesTitle
            } = result?.cookiesDescriptions;
            setCookiesDescriptions({
                analyticsCookiesInfo,
                analyticsCookiesTitle,
                essentialCookiesInfo,
                essentialCookiesTitle
            });
        }
    });

    const setMixPanelCookieCallback = useCallback(async (value) => {
        if (!value) {
            await MixPanelWrapper.RemoveMixPanel(true);
        } else {
            setCookie(REACT_APP_MIX_PANEL_INIT_COOKIE, true, {...cookieOption});
        }
        setCookie(REACT_APP_ACCEPTED_POLICY_COOKIE, true, {...cookieOption});
    }, [setCookie, REACT_APP_ACCEPTED_POLICY_COOKIE, cookieOption, REACT_APP_MIX_PANEL_INIT_COOKIE]);

    const setAllCookiesHandler = useCallback(() => {
        setShowPopup(false);
    }, []);

    const confirmChoiceHandler = useCallback(async () => {
        await setMixPanelCookieCallback(mixPanelCookies);
        setShowPopup(false);
        document.location.reload();
    }, [mixPanelCookies, setMixPanelCookieCallback]);

    const checkboxHandler = useCallback((value: boolean) => {
        setMixPanelCookies(value);
    }, []);

    const popupStyles = useMemo(() => ({
            padding: '24px 17px 24px 32px',
            flexDirection: 'row'
    }), []);

    return (
        <>
            <Container>
                {isCustomerLogged ? <span className={classes.popupTrigger}
                   onClick={() => setShowPopup(true)}
                >Manage Cookie Settings</span> : ''}
            </Container>
            <DataValidator data={contentConditionVar && !isLoading && !error}>
                <PopUp
                    showCloseButton={true}
                    type={'S'}
                    additionalStyles={popupStyles}
                    popUpTrigger={showPopup}
                    headingType={'h4'}
                    popUpClose={setAllCookiesHandler}
                    closeOnEsc={true}
                    closeMaskOnClick={true}
                    additionalClass={classes.CookiePolicyPopup}>
                    {contentStage === 1 && <FirstStage
								        title={mainTitle}
								        info={mainInfo}
								        setContentStage={setContentStage}
								        setAllCookiesHandler={setAllCookiesHandler} />}
                    {contentStage === 2 && <SecondStage
								        title={secondaryTitle}
								        info={secondaryInfo}
								        descriptions={cookiesDescriptions}
								        checkboxHandler={checkboxHandler}
								        confirmChoiceHandler={confirmChoiceHandler}
								        setAllCookiesHandler={setAllCookiesHandler} />}
                </PopUp>
            </DataValidator>
        </>
    );
};

export default withCookies(CookiePolicyPopup);
